<template>
    <pc-transition transition="slide">
        <div
            v-if="showBanner"
            v-bind:class="bannerClass"
            class="d-flex justify-content-center p-3">
            <slot />
            <div
                v-if="allowHideIndefinite"
                class="dropdown d-inline ms-3">
                <pc-button
                    id="dismissMenuBtn"
                    class="dropdown-toggle p-0 align-baseline"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    data-test-role="BannerDismissToggleBtn"
                    link>
                    {{ dismissButtonText }}
                </pc-button>
                <div
                    class="dropdown-menu"
                    aria-labelledby="dismissMenuBtn">
                    <a
                        class="dropdown-item"
                        href="#"
                        data-test-role="BannerHideBtn"
                        v-on:click="hide">
                        {{ hideButtonText }}
                    </a>
                    <a
                        class="dropdown-item"
                        href="#"
                        data-test-role="BannerHideIndefiniteBtn"
                        v-on:click="hideIndefinitely">
                        {{ dontShowAgainButtonText }}
                    </a>
                </div>
            </div>
            <div
                v-else
                class="d-inline ms-3">
                <a
                    href="#"
                    data-test-role="BannerHideBtn"
                    v-on:click="hide">
                    {{ hideButtonText }}
                </a>
            </div>
        </div>
    </pc-transition>
</template>
<script>
    import { BANNER_STYLE } from "./banners";
    import { KEYS, NAMESPACE } from "./i18n/dismissibleBanner.i18n";
    import { mapI18n } from "Util/i18n";

    const DEFAULT_AUTO_HIDE_AFTER_MS = 10000;
    const LOAD_COLS_DELAY_MS = 1000;
    const BANNER_HIDE_VALUE = "hide";
    const DEFAULT_HIDE_EXPIRES_AFTER_DAYS = 1 / 24; // 1 hour

    export default {
        props: {
            bannerModel: { type: Object, required: true }
        },
        data() {
            return {
                show: true,
                loadingCOLS: true,
                hiddenByUserCOLSValue: false
            };
        },
        computed: {
            showBanner() {
                return !this.loadingCOLS && this.show && !this.hiddenByUserCOLSValue;
            },
            allowHideIndefinite() {
                return this.bannerModel.AllowHideIndefinite;
            },
            bannerClass() {
                switch (this.bannerModel.BannerStyle) {
                    // Don't anticipate using the "error" style with this type of banner
                    case BANNER_STYLE.warning:
                        return "alert alert-warning";
                    default:
                        return "alert alert-primary";
                }
            },
            hideBannerCOLSKey() {
                return this.bannerModel.ShowHideCOLSKey;
            },
            autoHideAfterMs() {
                return this.bannerModel.AutoHideAfterMs || DEFAULT_AUTO_HIDE_AFTER_MS;
            },
            hideExpiresAfterDays() {
                return this.bannerModel.HideExpirationDays || DEFAULT_HIDE_EXPIRES_AFTER_DAYS;
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        created() {
            this.loadHiddenByUserCOLS();
            setTimeout(() => (this.show = false), this.autoHideAfterMs);

            // loadingCOLS initially blocks showing the banner while we wait for
            // COLS storage to be initialized. After a certain delay, set it to false
            // to show the banner unless we've been able to load a value from COLS
            // or a cookie telling us not to show the banner.
            setTimeout(() => {
                this.loadingCOLS = false;
            }, LOAD_COLS_DELAY_MS);
        },
        methods: {
            hide() {
                const expirationDate = new Date();
                expirationDate.setDate(expirationDate.getDate() + this.hideExpiresAfterDays);
                this.$app.setCOLS_item(this.hideBannerCOLSKey, {
                    action: BANNER_HIDE_VALUE,
                    expiresAfter: expirationDate
                });
                this.show = false;
            },
            hideIndefinitely() {
                if (!this.allowHideIndefinite) {
                    return;
                }

                const expirationDate = new Date();
                expirationDate.setFullYear(
                    expirationDate.getFullYear() + 1,
                    expirationDate.getMonth(),
                    expirationDate.GetDate()
                );
                this.$app.setCOLS_item(this.hideBannerCOLSKey, {
                    action: BANNER_HIDE_VALUE,
                    expiresAfter: expirationDate
                });
                this.show = false;
            },
            // Check COLS (cross-origin local storage) to see if user has hidden
            // banner for another subdomain. There's no guarantee we'll be able to get
            // any value from COLS, so after a short delay, we'll show the banner by default
            // unless we've loaded a value that tells us not to from COLS or a cookie.
            async loadHiddenByUserCOLS() {
                const result = await this.$app.getCOLS_item(this.hideBannerCOLSKey);

                // If we're within the initial timeout where we haven't yet shown the banner,
                // if the COLS value exists, is well-formed, and we haven't passed the expiration
                // time, then listen to the value of the hide banner preference in the COLS value
                if (
                    this.loadingCOLS &&
                    Boolean(result) &&
                    result.action === BANNER_HIDE_VALUE &&
                    Boolean(result.expiresAfter) &&
                    new Date().getTime() < new Date(result.expiresAfter).getTime()
                ) {
                    this.hiddenByUserCOLSValue = true;
                }
            }
        }
    };
</script>

<style></style>
